import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import ScanningService from "app/data/dock-scan/scanningService";
import {
  AuthAuditEventsQuery,
  CreateDockEmployeeRequest,
  CreateDockTagRequest,
  DockEmployeeStatus,
  ScanLogsQuery,
  UpdateDockEmployeeRequest,
} from "app/data/dock-scan/models";

const scanningService = ScanningService.getInstance();
const EMPLOYEES_PAGE_SIZE = 100;

export function useDockOperationsSummary(terminalNumber?: number) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_OPERATIONS_SUMMARY, terminalNumber],
    queryFn: () => scanningService.getSummary(terminalNumber),
    enabled: terminalNumber != null,
    refetchOnWindowFocus: false,
  });
}

export function useDockActiveSessions(terminalNumber?: number, refetchInterval?: number) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_ACTIVE_SESSIONS, terminalNumber],
    queryFn: () => scanningService.getActiveSessions(terminalNumber),
    enabled: terminalNumber != null,
    refetchInterval,
    refetchOnWindowFocus: false,
  });
}

/** Cursor-paginated employees for a terminal (infinite). */
export function useDockEmployees(
  terminalNumber?: number,
  status?: DockEmployeeStatus | "",
  tagIds?: string[]
) {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCK_EMPLOYEES, terminalNumber, status, tagIds],
    queryFn: ({ pageParam }) =>
      scanningService.listEmployees({
        terminalNumber,
        ...(status ? { status } : {}),
        ...(tagIds && tagIds.length > 0 ? { tagIds } : {}),
        take: EMPLOYEES_PAGE_SIZE,
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: terminalNumber != null,
    refetchOnWindowFocus: false,
  });
}

export function useDockEmployee(employeeId?: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_EMPLOYEE_DETAIL, employeeId],
    queryFn: () => scanningService.getEmployee(employeeId!),
    enabled: !!employeeId,
    refetchOnWindowFocus: false,
  });
}

export function useDockTags(terminalNumber?: number) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_TAGS, terminalNumber],
    queryFn: () => scanningService.listTags(terminalNumber!),
    enabled: terminalNumber != null,
    refetchOnWindowFocus: false,
  });
}

export function useDockLastLogins(terminalNumber?: number) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_LAST_LOGINS, terminalNumber],
    queryFn: () => scanningService.getLastLogins(terminalNumber, 100),
    enabled: terminalNumber != null,
    refetchOnWindowFocus: false,
  });
}

export function useDockAuthEvents(query: AuthAuditEventsQuery, enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_AUTH_EVENTS, query],
    queryFn: () => scanningService.getAuthEvents(query),
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useDockAuthEventsInfinite(
  query: Omit<AuthAuditEventsQuery, "cursor">,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.DOCK_AUTH_EVENTS, "infinite", query],
    queryFn: ({ pageParam }) =>
      scanningService.getAuthEvents({
        ...query,
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useDockScanLogs(query: ScanLogsQuery, enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCK_SCAN_LOGS, query],
    queryFn: () => scanningService.listScanLogs(query),
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useDockScanMutations(terminalNumber?: number) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_EMPLOYEES] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_TAGS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_EMPLOYEE_DETAIL] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_OPERATIONS_SUMMARY] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_ACTIVE_SESSIONS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_LAST_LOGINS] });
    if (terminalNumber != null) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCK_EMPLOYEES, terminalNumber],
      });
    }
  };

  const createEmployee = useMutation({
    mutationFn: (body: CreateDockEmployeeRequest) => scanningService.createEmployee(body),
    onSuccess: invalidate,
  });

  const updateEmployee = useMutation({
    mutationFn: ({
      employeeId,
      body,
    }: {
      employeeId: string;
      body: UpdateDockEmployeeRequest;
    }) => scanningService.updateEmployee(employeeId, body),
    onSuccess: invalidate,
  });

  const deactivateEmployee = useMutation({
    mutationFn: (employeeId: string) => scanningService.deactivateEmployee(employeeId),
    onSuccess: invalidate,
  });

  const createTag = useMutation({
    mutationFn: (body: CreateDockTagRequest) => scanningService.createTag(body),
    onSuccess: invalidate,
  });

  const deleteTag = useMutation({
    mutationFn: (tagId: string) => scanningService.deleteTag(tagId),
    onSuccess: invalidate,
  });

  return {
    createEmployee,
    updateEmployee,
    deactivateEmployee,
    createTag,
    deleteTag,
    invalidate,
  };
}
